import ReactDOMServer from 'react-dom/server'
import { replace } from 'lodash'

import { useI18n } from '@ggs/i18n'
import { selectSearchResult, setSearchDisplayMode, useDispatch } from '@ggs/store'

import { DesktopWindowsOutlined, LocationOnOutlined } from '@mui/icons-material'
import { Typography } from '@mui/material'

import { Button } from '../../buttons'
import { TextClamp } from '../../text'
import { calcDistance, formatUrl, formatEmail, formatPhone } from '../../../local/utils'

import './SearchResult.scss'


/**
 * @typedef {object} SearchResultProps
 * @property {import("../../../definitions/Search211ApiTypes").Search211Record} data
 * @property {number} lat
 * @property {number} lng
 * @property {number} index
 */

/**
 * @param {SearchResultProps} props
 * @return {JSX.Element}
 */
const SearchResult = ({ data, lat, lng, index }) => {
  const { t } = useI18n()
  const dispatch = useDispatch()
  const distance =
    data.Latitude !== 0 && data.Longitude !== 0
      ? new Intl.NumberFormat('en-CA', {
          style: 'unit',
          unit: 'kilometer',
          maximumFractionDigits: 1,
        }).format(calcDistance(lat, lng, data.Latitude, data.Longitude))
      : '-- KM'

  const isPhysical = data.PhysicalAddressStreet1 !== '' || data.PhysicalAddressStreet2 !== ''
  const city = data.PhysicalAddressCity || data.MailingAddressCity || data.ServiceArea[0]

  /**
   * Remove last <br> from html string
   * @param {string} str
   * @return {string}
   */
  const trim = (str) => str.replace(/^\s*<br\s*\/?>|<br\s*\/?>\s*$/g, '')

  /**
   *
   * @param {string} str
   * @param {string} strToReplace
   * @param {string?} replaceStr
   * @return {string}
   */
  const addBold = (str, strToReplace, replaceStr) =>
    str.replace(
      strToReplace,
      `<span class="search-result__eligibility--heading">${replaceStr ? replaceStr : strToReplace}</span>`
    )

  /**
   *
   * @param {string} str
   * @return {string}
   */
  const addLinkRel = (str) => replace(str, new RegExp('<a', 'g'), '<a rel="noreferrer noopener"')

  /**
   *
   * @param {string} str
   * @return {string}
   */
  const formatLists = (str) => str.replace(/ \* /g, '<br/>')

  /**
   * Remove extra spacing from html string
   * @param {string} str
   * @return {string}
   */
  const removeExtraSpacing = (str) => str.replace(/(\s?<br\s?\/?>\s?){3,}/g, '<br/><br/>')

  /**
   * Format html string
   * @param {string} str
   * @return {string}
   */
  const formatHtml = (str) => {
    return removeExtraSpacing(formatLists(trim(addLinkRel(str))))
  }

  /**
   * Mark result index as selected (used to highlight) and transition to map view.
   */
  const handleViewResultInMap = () => {
    if (data) {
      dispatch(selectSearchResult(data.id))
      dispatch(setSearchDisplayMode('map'))
    }
  }

  const storeDistance = () => {
    const distances = localStorage.getItem('resultDistances')
    const distanceMap = new Map(distances ? Object.entries(JSON.parse(distances)) : undefined)
    distanceMap.set(`${data.id}`, distance)
    localStorage.setItem('resultDistances', JSON.stringify(Object.fromEntries(distanceMap)))
  }

  const descriptionHtml = formatHtml(data.Description)

  const eligibilityHtml = data.Eligibility ? ReactDOMServer.renderToStaticMarkup(
    <div className="search-result__eligibility">
      <p
        dangerouslySetInnerHTML={{
          __html: `<span class="search-result__eligibility--heading">${t('Eligibility: ')}</span> ${addBold(
            formatHtml(data.Eligibility),
            'Residency Requirements:', t('Residency Requirements: ')
          )}`,
        }}
      />
    </div>
  ) : ''

  const htmlContent = `<p>${descriptionHtml}</p> ${eligibilityHtml}`

  return (
    <article className="search-result" onMouseEnter={storeDistance}>
      <header className="search-result__header">
        <div className="search-result__pre-content">
          <Typography
            color="primary"
            style={{ fontWeight: 600 }}
            className="search-result__distance"
          >
            <span>{distance}</span>
            <span>&#8729;</span>
            <span>{city}</span>
          </Typography>
          {isPhysical ? (
            <Button
              onClick={handleViewResultInMap}
              variant="text"
              color="primary"
              buttonFields={{
                startIcon: <LocationOnOutlined />,
              }}
              classes={{
                text: 'search-result__button',
              }}
            >
              {t('View on Map')}
            </Button>
          ) : (
            <Button
              variant="text"
              color="primary"
              buttonFields={{
                disableRipple: true,
                tabIndex: -1,
                startIcon: <DesktopWindowsOutlined />,
              }}
              classes={{
                text: 'search-result__button search-result__button--no-hover',
                startIcon: 'search-result__start-icon'
              }}
            >
              {t('Digital Service Only')}
            </Button>
          )}
        </div>
        <Typography component="h3" color="primary" className="search-result__title">
          <a href={`/result/view/${data.id}`}>{index}. {data.PublicName}</a>
        </Typography>
      </header>
      <div className="search-result__description">
        <TextClamp htmlContent={htmlContent} maxLines={3} />
      </div>
      {(data.PhoneNumbers[0] || data.Email || data.Website) && (
        <Typography component="address" color="primary" className="search-result__details">
          {data.PhoneNumbers[0] && (
            <span className="search-result__phone">{t('T: ')} {formatPhone(data.PhoneNumbers)}</span>
          )}
          {data.Email && (
            <span className="search-result__email">
              {t('E: ')}
              <a href={formatEmail(data.Email)} target="_blank" rel="noreferrer noopener">
                {data.Email}
              </a>
            </span>
          )}
          {data.Website && (
            <span className="search-result__website">
              <a href={formatUrl(data.Website)} target="_blank" rel="noreferrer noopener">
                {t('Visit Website')}
              </a>
            </span>
          )}
        </Typography>
      )}
    </article>
  )
}

export default SearchResult
